import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import {
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import {
  IActivo,
  IFilter,
  IMaterialServicioTecnico,
  IQueryParam,
  IUsuario,
} from 'modelos/src';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { EventosGenericosHttpService } from '../../../../../auxiliares/servicios/http/eventos-genericos.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { ActivosService } from '../../../../../auxiliares/servicios/http/activos.service';
import { MaterialesServicioTecnicoService } from '../../../../../auxiliares/servicios/http/materiales-servicio-tecnico.service';
import {
  AsignarMaterialesTecnicoComponent,
  DataAsignarMaterialesTecnico,
} from '../../../../standalone/asignar-materiales-tecnico/asignar-materiales-tecnico.component';
import { IUsuarioConPermiso } from '../../../../../auxiliares/servicios/http/usuarios.service';
import { tecnicoAtiendeTipo } from '../../../../../auxiliares/servicios/especialidad-tecnico';
import {
  CrearEditarUsuarioFuncionComponent,
  DataUsuarioFuncion,
} from '../../../../standalone/usuarios-por-funcion/crear-editar-usuario-funcion/crear-editar-usuario-funcion.component';

@Component({
  selector: 'app-asignar-generico',
  imports: [AuxiliaresModule],
  templateUrl: './asignar.component.html',
  styleUrl: './asignar.component.scss',
})
export class AsignarGenericoComponent implements OnInit, OnDestroy {
  private formularios = inject(FormularioDialogService);
  matDialogRef = inject<MatDialogRef<AsignarGenericoComponent>>(MatDialogRef);
  private lista2 = inject(Lista2Service);
  private dialogService = inject(DialogService);
  private service = inject(EventosGenericosHttpService);
  private helper = inject(HelperService);
  private dialog = inject(MatDialog);
  private activosService = inject(ActivosService);
  private materialesService = inject(MaterialesServicioTecnicoService);

  public loading = signal(false);
  // Component
  @Input() public id?: string;
  @Input() public idTecnico?: string;
  // Dialog
  public data = inject(MAT_DIALOG_DATA) as {
    id?: string;
    idTecnico?: string;
  };

  /// Data
  public tecnicos: (IUsuario & { label: string })[] = [];
  public idTecnicoSeleccionado?: string;
  public tecnico?: IUsuario;
  public fechaDisponibleParaTratar?: Date;
  public tecnicos$?: Subscription;
  // Cliente del servicio técnico: solo se listan técnicos de ese cliente.
  private idClienteEvento?: string;
  // Tipo del ST: solo se listan técnicos con la especialidad (módulo) que lo atiende.
  private tipoEventoST?: string;
  // Si está activo, lista todos los técnicos del scope (no solo los del cliente).
  public verTodos = false;

  // Vehículo: el técnico queda vinculado como conductor (vehiculo.idChofer),
  // que es lo que usa el mapa de Servicio Técnico para ubicarlo.
  public moduloVehiculos = this.helper.tieneModulo('Vehículos');
  public puedeAsignarVehiculo = this.helper.tienePermiso(
    'Vehículos - Asignar conductor',
  );
  // Vehículo del que el técnico seleccionado ya es conductor (cualquier función).
  public vehiculoActual?: IActivo;
  // Vehículos con función Servicio Técnico del cliente del técnico.
  public vehiculos: (IActivo & { label: string })[] = [];
  // Opcional: sin elegir, el vehículo del técnico no se toca.
  public idVehiculoSeleccionado?: string;
  // Como verTodos de técnicos: lista los de todo el scope, no solo del cliente.
  public verTodosVehiculos = false;
  public cargandoVehiculos = false;
  // El técnico que viene preasignado necesita la lista de técnicos (de ahí sale
  // su cliente): sus vehículos se cargan con la primera respuesta, una sola vez.
  private vehiculosIniciados = false;

  // Materiales: stock asignado al técnico (tipo 'asignado'), como en el
  // listado de técnicos. Asignar lo exige el backend con este permiso.
  public materialesTecnico: { nombre: string; cantidad: number }[] = [];
  public cargandoMateriales = false;
  public puedeAsignarMateriales = this.helper.tienePermiso(
    'Servicio Técnico - Gestionar pedidos de materiales',
  );

  public get vehiculoSeleccionado() {
    return this.vehiculos.find((v) => v._id === this.idVehiculoSeleccionado);
  }

  // Se asigna solo si cambia: elegir el vehículo que ya tiene no hace nada.
  private get vehiculoAAsignar() {
    if (!this.idTecnicoSeleccionado) return undefined;
    const v = this.vehiculoSeleccionado;
    return v && v._id !== this.vehiculoActual?._id ? v : undefined;
  }

  public nombreUsuario(u?: IUsuario): string {
    return u?.datosPersonales?.nombre || u?.usuario || '';
  }

  // Lo que cambia al asignar el vehículo elegido. El backend (asignarChofer)
  // reemplaza al conductor que tenga y saca al técnico de su vehículo anterior.
  public avisosVehiculo(): string[] {
    const v = this.vehiculoAAsignar;
    if (!v) return [];
    const avisos: string[] = [];
    const chofer = v.vehiculo?.chofer;
    if (chofer && v.vehiculo?.idChofer !== this.idTecnicoSeleccionado) {
      avisos.push(
        `${this.nombreUsuario(chofer)} deja de ser el conductor de ${v.label}.`,
      );
    }
    if (this.vehiculoActual) {
      avisos.push(
        `El técnico deja de ser el conductor de ${this.labelVehiculoActual()}.`,
      );
    }
    return avisos;
  }

  public async asignar() {
    if (!this.id && !this.data) {
      return;
    }
    const vehiculo = this.vehiculoAAsignar;
    let sentence;
    if (!this.tecnico) {
      sentence = `¿Confirmás desasignar el servicio técnico?`;
    } else {
      sentence = `¿Confirmás asignar el Servicio Técnico a ${this.nombreUsuario(this.tecnico)}?`;
      if (vehiculo) {
        sentence += ` También quedará como conductor de ${vehiculo.label}.`;
        for (const aviso of this.avisosVehiculo()) sentence += ` ${aviso}`;
      }
    }
    const res = await this.dialogService.confirm('Asignación', sentence);
    if (!res) {
      return;
    }
    try {
      this.loading.set(true);
      const id = this.id || this.data.id;
      const idTecnico = this.idTecnicoSeleccionado || null;

      // Para eventos genéricos, usamos update con detallesTecnicos
      const fecha = this.fechaDisponibleParaTratar?.toISOString();
      await this.service.asignar(id, idTecnico, fecha);

      if (vehiculo && idTecnico) {
        try {
          await this.activosService.asignarChofer(vehiculo._id!, idTecnico);
        } catch (error) {
          // El ST ya quedó asignado: se avisa solo lo del vehículo.
          console.error(error);
          this.helper.notifError(
            'El servicio técnico se asignó, pero no se pudo asignar el vehículo',
          );
          this.cerrar();
          return;
        }
      }

      this.helper.notifSuccess(
        vehiculo
          ? 'Servicio Técnico y vehículo asignados correctamente'
          : 'Servicio Técnico asignado correctamente',
      );
      this.cerrar();
    } catch (error) {
      this.helper.notifError(error);
      console.error(error);
    } finally {
      this.loading.set(false);
    }
  }

  public async desasignar() {
    if (!this.id && !this.data) {
      return;
    }
    const res = await this.dialogService.confirm(
      'Desasignar Técnico',
      `¿Confirmás desasignar el servicio técnico?`,
    );
    if (!res) {
      return;
    }
    try {
      this.loading.set(true);
      const id = this.id || this.data.id;

      await this.service.asignar(id, null);

      this.helper.notifSuccess('Servicio Técnico desasignado correctamente');
      this.cerrar();
    } catch (error) {
      this.helper.notifError(error);
      console.error(error);
    } finally {
      this.loading.set(false);
    }
  }

  private cerrar() {
    this.matDialogRef.close(true);
  }

  public cerrarDialog() {
    this.matDialogRef.close();
  }

  public async crear() {
    const data: DataUsuarioFuncion = {
      editando: false,
      funcion: 'Técnico',
    };
    const ref = this.formularios.abrir(CrearEditarUsuarioFuncionComponent, {
      tamano: 'grande',
      data,
    });
    // Al cerrar, recargar el listado para que aparezca el técnico recién creado.
    ref.afterClosed().subscribe(() => this.listar());
  }

  // Etiqueta visible/buscable: "Nombre completo (usuario)". Sin nombre cae al usuario.
  private armarLabel(u: IUsuario): string {
    const nombre = u.datosPersonales?.nombre?.trim();
    return nombre ? `${nombre} (${u.usuario})` : (u.usuario ?? '');
  }

  private async listar(): Promise<void> {
    // Solo técnicos del cliente del servicio técnico. includeChildren:true para
    // que el scope alcance al cliente (si es hijo). El scope lo aplica el backend
    // (query.filter + permiso); no se re-filtra por u.idCliente (deprecado y vacío
    // en técnicos auto-registrados). El sort se hace acá.
    const idCliente = this.verTodos ? undefined : this.idClienteEvento;
    const query: IQueryParam = {
      limit: 0,
      includeChildren: true,
      childrenLevel: 0,
      filter: JSON.stringify(idCliente ? { idCliente } : {}),
    };
    this.tecnicos$?.unsubscribe();
    this.tecnicos$ = this.lista2.tecnicos(query, 30).subscribe((data) => {
      this.tecnicos = (data.datos || [])
        // Solo técnicos con la especialidad (módulo) que atiende este tipo de ST.
        .filter((u) =>
          tecnicoAtiendeTipo(
            (u as IUsuarioConPermiso).permiso,
            this.tipoEventoST,
          ),
        )
        .map((u) => ({ ...u, label: this.armarLabel(u) }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
        );
      if (!this.vehiculosIniciados) {
        this.vehiculosIniciados = true;
        // El preasignado viene solo como id: sin esto la confirmación de
        // asignar() lo tomaba como desasignación.
        this.tecnico ??= this.tecnicos.find(
          (t) => t._id === this.idTecnicoSeleccionado,
        );
        this.cargarVehiculos();
        this.cargarMateriales();
      }
    });
  }

  // Materiales asignados al técnico seleccionado, agrupados por nombre.
  private async cargarMateriales(): Promise<void> {
    this.cargandoMateriales = false;
    this.materialesTecnico = [];
    const idUsuario = this.idTecnicoSeleccionado;
    if (!idUsuario) return;
    const filter: IFilter<IMaterialServicioTecnico> = {
      idUsuario,
      tipo: 'asignado',
      estado: 'Asignado',
    };
    this.cargandoMateriales = true;
    try {
      const res = await this.materialesService.getFiltered({
        filter: JSON.stringify(filter),
        unlimited: true,
        includeChildren: true,
      });
      if (this.idTecnicoSeleccionado !== idUsuario) return;
      const map = new Map<string, number>();
      for (const m of res.datos || []) {
        const nombre = m.descripcion || m.idEntidad || 'Material';
        map.set(nombre, (map.get(nombre) ?? 0) + (m.cantidad ?? 1));
      }
      this.materialesTecnico = [...map.entries()].map(([nombre, cantidad]) => ({
        nombre,
        cantidad,
      }));
    } catch (error) {
      console.error('No se pudieron cargar los materiales del técnico', error);
    } finally {
      if (this.idTecnicoSeleccionado === idUsuario) {
        this.cargandoMateriales = false;
      }
    }
  }

  // Mismo diálogo que el listado de técnicos. Necesita el técnico de la lista
  // (trae el permiso, de donde sale su cliente).
  public abrirAsignarMateriales(): void {
    const tecnico = this.tecnico;
    if (!tecnico?._id) return;
    const data: DataAsignarMaterialesTecnico = { usuario: tecnico };
    this.formularios
      .abrir(AsignarMaterialesTecnicoComponent, {
        tamano: 'grande',
        data,
      })
      .afterClosed()
      .subscribe(() => this.cargarMateriales());
  }

  private armarLabelVehiculo(v: IActivo): string {
    const patente = v.vehiculo?.patente;
    return patente && patente !== v.identificacion
      ? `${v.identificacion} (${patente})`
      : (v.identificacion ?? '');
  }

  // Vehículo actual del técnico seleccionado y, si se puede asignar, los
  // vehículos con función Servicio Técnico del cliente del técnico (o de todo
  // el scope con verTodosVehiculos).
  public async cargarVehiculos(): Promise<void> {
    this.cargandoVehiculos = false;
    this.vehiculoActual = undefined;
    this.vehiculos = [];
    this.idVehiculoSeleccionado = undefined;
    const idTecnico = this.idTecnicoSeleccionado;
    if (!this.moduloVehiculos || !idTecnico) return;
    // Cliente del permiso de técnico; si no está en la lista, el del ST.
    const idCliente =
      this.tecnicos.find((t) => t._id === idTecnico)?.idCliente ||
      this.idClienteEvento;
    const base: IQueryParam = {
      select: 'identificacion funcion idCliente vehiculo',
      populate: JSON.stringify([
        { path: 'vehiculo.chofer', select: 'usuario datosPersonales' },
        { path: 'cliente', select: 'nombre' },
      ]),
      includeChildren: true,
    };
    const filtroServicio = this.verTodosVehiculos
      ? { categoria: 'Vehículo', funcion: 'Servicio Técnico' }
      : idCliente
        ? { categoria: 'Vehículo', funcion: 'Servicio Técnico', idCliente }
        : undefined;
    this.cargandoVehiculos = true;
    try {
      const [actual, deServicio] = await Promise.all([
        this.activosService.getFiltered({
          ...base,
          filter: JSON.stringify({
            categoria: 'Vehículo',
            'vehiculo.idChofer': idTecnico,
          }),
          limit: 1,
        }),
        this.puedeAsignarVehiculo && filtroServicio
          ? this.activosService.getFiltered({
              ...base,
              filter: JSON.stringify(filtroServicio),
              limit: 0,
              unlimited: true,
            })
          : undefined,
      ]);
      // Si mientras cargaba se eligió otro técnico, esta respuesta ya no sirve.
      if (this.idTecnicoSeleccionado !== idTecnico) return;
      this.vehiculoActual = actual.datos?.[0];
      this.vehiculos = (deServicio?.datos || [])
        .map((v) => ({ ...v, label: this.armarLabelVehiculo(v) }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
        );
    } catch (error) {
      console.error('No se pudieron cargar los vehículos del técnico', error);
    } finally {
      // Una respuesta vieja no apaga el spinner de la carga en curso.
      if (this.idTecnicoSeleccionado === idTecnico) {
        this.cargandoVehiculos = false;
      }
    }
  }

  public labelVehiculoActual(): string {
    return this.vehiculoActual
      ? this.armarLabelVehiculo(this.vehiculoActual)
      : '';
  }

  public onChange(tecnico: IUsuario) {
    this.tecnico = tecnico;
    this.idTecnicoSeleccionado = tecnico?._id;
    this.cargarVehiculos();
    this.cargarMateriales();
  }

  public onClear() {
    this.tecnico = undefined;
    this.idTecnicoSeleccionado = undefined;
    this.cargarVehiculos();
    this.cargarMateriales();
  }

  public async cambiarVerTodos() {
    await this.listar();
  }

  //// HOOKS

  async ngOnInit() {
    const id = this.data?.id || this.id;
    if (id) {
      this.idTecnicoSeleccionado = this.data?.idTecnico || this.idTecnico;
      // Resolver el cliente del servicio técnico para acotar el listado.
      try {
        const evento = await this.service.getById(id);
        this.idClienteEvento = evento?.idCliente;
        this.tipoEventoST = evento?.tipoEvento;
      } catch (error) {
        console.error(
          'No se pudo obtener el cliente del servicio técnico',
          error,
        );
      }
    }
    this.fechaDisponibleParaTratar = new Date();
    await this.listar();
  }

  ngOnDestroy() {
    this.tecnicos$?.unsubscribe();
  }
}
